
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

export type SortOption = 'reviewCount' | 'rating' | 'alphabetical' | 'proximity' | 'distance';
export type SortDirection = 'asc' | 'desc';

interface SortingOptionsProps {
  onSortChange: (option: SortOption, direction: SortDirection) => void;
  showProximityOption?: boolean;
  defaultOption?: SortOption;
  defaultDirection?: SortDirection;
}

export const SortingOptions = ({ 
  onSortChange, 
  showProximityOption = false,
  defaultOption = 'reviewCount',
  defaultDirection = 'desc'
}: SortingOptionsProps) => {
  const { t, language } = useLanguage();
  const [selectedOption, setSelectedOption] = useState<SortOption>(defaultOption);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);
  
  // Apply default options when they change
  useEffect(() => {
    setSelectedOption(defaultOption);
    setSortDirection(defaultDirection);
  }, [defaultOption, defaultDirection]);

  // Apply initial sort on mount - this ensures the initial sort happens once
  useEffect(() => {
    console.log('Initial sort applied:', selectedOption, sortDirection);
    onSortChange(selectedOption, sortDirection);
  }, []); 

  const handleSortChange = (option: SortOption) => {
    console.log('Sort option changed:', option);
    setSelectedOption(option);
  };

  const handleDirectionChange = (direction: SortDirection) => {
    console.log('Sort direction changed:', direction);
    setSortDirection(direction);
  };

  const handleStartSort = () => {
    console.log('Sort button clicked, applying sort:', selectedOption, sortDirection);
    onSortChange(selectedOption, sortDirection);
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center mb-6">
      <label className="text-sm font-medium text-gray-700">
        {language === 'de' ? 'Sortieren nach:' : 'Sort by:'}
      </label>
      <div className="flex gap-2 flex-wrap">
        <Select value={selectedOption} onValueChange={(value) => handleSortChange(value as SortOption)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="reviewCount">
              {language === 'de' ? 'Anzahl der Bewertungen' : 'Number of Reviews'}
            </SelectItem>
            <SelectItem value="rating">
              {language === 'de' ? 'Durchschnittliche Bewertung' : 'Average Rating'}
            </SelectItem>
            <SelectItem value="alphabetical">
              {language === 'de' ? 'Alphabetisch' : 'Alphabetical'}
            </SelectItem>
            {showProximityOption && (
              <SelectItem value="proximity">
                {language === 'de' ? 'Nähe zu Postleitzahl' : 'Proximity to Postal Code'}
              </SelectItem>
            )}
            <SelectItem value="distance">
              {language === 'de' ? 'Entfernung' : 'Distance'}
            </SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortDirection} onValueChange={(value) => handleDirectionChange(value as SortDirection)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">
              {t('sortDescending')}
            </SelectItem>
            <SelectItem value="asc">
              {t('sortAscending')}
            </SelectItem>
          </SelectContent>
        </Select>
        
        <Button onClick={handleStartSort} className="whitespace-nowrap">
          {language === 'de' ? 'Sortierung starten' : 'Start Sorting'}
        </Button>
      </div>
    </div>
  );
};
